import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Divider, Input, Button, Select, Spin, Space, Card } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import openNotification from "components/utils/CustomNotification";
import "../../styles/AddSystemConfig.css";

const { Option } = Select;

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

interface FieldItem {
  name: string;
  type: string;
  db_key: string;
  method: string;
  mandatory?: boolean;
  values?: Record<string, any>;
  interprete?: string;
  key_type?: string;
  value_type?: string;
}

interface TabData {
  tab_id: string;
  tab_name: string;
  interprete?: string;
  filter: Record<string, FieldItem>;
  default_objects?: any[];
}

const AddSystemConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState<TabData[]>([]);
  const [hrInfo, setHrInfo] = useState<Record<string, any>>({});
  const [readOnly, setReadOnly] = useState(false);
  const [mode, setMode] = useState<"add" | "view" | "edit">("add");
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const isTokenTab = (tabId: string) => tabId?.toLowerCase().includes("token");

  useEffect(() => {
    if (location.state?.mode === "view" && location.state?.config?.name) {
      const hrName = location.state.config.name;
      setReadOnly(true);
      setMode("view");
      getHrConfigsByName(hrName);
    }
  }, [location.state]);

  const getHrConfigFilter = async () => {
    if (!sessionUid || !userUid) return;
    try {
      setLoading(true);
      const res = await axios.get(`${beAddress}/api/hr-config-filter`, {
        headers: {"user-uid": userUid },withCredentials: true,
      });

      if (res.data?.status === "Success" && Array.isArray(res.data.data)) {
        setTabs(res.data.data);

        if (!location.state?.config) {
          const initial: Record<string, any> = {};
          res.data.data.forEach((tab: TabData) => {
            if (tab.interprete === "group_of_objects") {
              initial[tab.tab_id] = [];
            } else {
              Object.entries(tab.filter).forEach(([_, field]) => {
                if (field.interprete === "key_value_object") {
                  initial[field.db_key] = {};
                } else {
                  initial[field.db_key] = "";
                }
              });
            }
          });

          if (!initial.token_endpoint) {
            initial.token_endpoint = {
              enable: "",
              uri: "",
              response_type: "",
              query_params: {},
            };
          }

          setHrInfo(initial);
        }
      }
    } catch (err) {
      console.error("Error fetching HR config fields:", err);
    } finally {
      setLoading(false);
    }
  };

  const getHrConfigsByName = async (hrName: string) => {
    if (!sessionUid || !userUid || !hrName) return;
    try {
      setLoading(true);
      const res = await axios.get(`${beAddress}/api/hr-configs`, {
        headers: {"user-uid": userUid },withCredentials: true,
        params: { portal_type: hrName },
      });

      if (res.data?.status === "Success" && res.data?.data) {
        const data =
          Array.isArray(res.data.data) && res.data.data.length > 0
            ? res.data.data[0]
            : res.data.data;
        setHrInfo(data);
      }
    } catch (err) {
      console.error("Error fetching HR config by name:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHrConfigFilter();
  }, []);

  const handleChange = (dbKey: string, value: any) =>
    setHrInfo((prev) => ({ ...prev, [dbKey]: value }));

  const handleTokenChange = (dbKey: string, value: any) =>
    setHrInfo((prev) => ({
      ...prev,
      token_endpoint: { ...prev.token_endpoint, [dbKey]: value },
    }));

  const handleQueryParamChange = (
    parentKey: string,
    key: string,
    field: "key" | "value",
    newValue: any,
    index?: number
  ) => {
    if (readOnly) return;
    setHrInfo((prev) => {
      const updated = { ...prev };
      if (typeof index === "number") {
        const endpoints = [...(updated["endpoints"] || [])];
        const params = { ...(endpoints[index].query_params || {}) };
        if (field === "key") {
          const oldVal = params[key];
          delete params[key];
          params[newValue] = oldVal || "";
        } else {
          params[key] = newValue;
        }
        endpoints[index].query_params = params;
        updated["endpoints"] = endpoints;
      } else if (parentKey === "token_endpoint_query_params") {
        const tokenParams = { ...(updated.token_endpoint?.query_params || {}) };
        if (field === "key") {
          const oldVal = tokenParams[key];
          delete tokenParams[key];
          tokenParams[newValue] = oldVal || "";
        } else {
          tokenParams[key] = newValue;
        }
        updated.token_endpoint = {
          ...updated.token_endpoint,
          query_params: tokenParams,
        };
      } else {
        const params = { ...(prev[parentKey] || {}) };
        if (field === "key") {
          const oldVal = params[key];
          delete params[key];
          params[newValue] = oldVal || "";
        } else {
          params[key] = newValue;
        }
        updated[parentKey] = params;
      }
      return updated;
    });
  };

  const addQueryParam = (dbKey: string, index?: number) => {
    if (readOnly) return;
    setHrInfo((prev) => {
      const updated = { ...prev };
      if (typeof index === "number") {
        const endpoints = [...(updated["endpoints"] || [])];
        const currentParams = { ...(endpoints[index].query_params || {}) };
        currentParams[""] = "";
        endpoints[index].query_params = currentParams;
        updated["endpoints"] = endpoints;
      } else if (dbKey === "token_endpoint_query_params") {
        const tokenParams = {
          ...(prev.token_endpoint?.query_params || {}),
          "": "",
        };
        updated.token_endpoint = {
          ...prev.token_endpoint,
          query_params: tokenParams,
        };
      } else {
        updated[dbKey] = { ...(prev[dbKey] || {}), "": "" };
      }
      return updated;
    });
  };

  const deleteQueryParam = (dbKey: string, key: string, index?: number) => {
    if (readOnly) return;
    setHrInfo((prev) => {
      const updated = { ...prev };
      if (typeof index === "number") {
        const endpoints = [...(updated["endpoints"] || [])];
        const params = { ...(endpoints[index].query_params || {}) };
        delete params[key];
        endpoints[index].query_params = params;
        updated["endpoints"] = endpoints;
      } else if (dbKey === "token_endpoint_query_params") {
        const tokenParams = { ...(prev.token_endpoint?.query_params || {}) };
        delete tokenParams[key];
        updated.token_endpoint = {
          ...prev.token_endpoint,
          query_params: tokenParams,
        };
      } else {
        const params = { ...(prev[dbKey] || {}) };
        delete params[key];
        updated[dbKey] = params;
      }
      return updated;
    });
  };

  const addDataEndpoint = () => {
    if (readOnly) return;
    setHrInfo((prev) => ({
      ...prev,
      endpoints: [
        ...(prev["endpoints"] || []),
        { enable: "", uri: "", response_type: "", query_params: {} },
      ],
    }));
  };

  const deleteDataEndpoint = (index: number) => {
    if (readOnly) return;
    setHrInfo((prev) => {
      const endpoints = [...(prev["endpoints"] || [])];
      endpoints.splice(index, 1);
      return { ...prev, endpoints };
    });
  };

  const renderKeyValueObject = (
    field: FieldItem,
    index?: number,
    dbKey?: string
  ) => {
    const keyName = dbKey || field.db_key;
    let params: Record<string, any> = {};
    if (typeof index === "number") {
      params = hrInfo["endpoints"]?.[index]?.query_params || {};
    } else if (keyName === "token_endpoint_query_params") {
      params = hrInfo.token_endpoint?.query_params || {};
    } else {
      params = hrInfo[keyName] || {};
    }

    const valueOptions = Object.entries(field.values || {});

    return (
      <div className="key-value-container">
        {Object.entries(params).map(([key, val], i) => (
          <Space key={i} className="key-value-row">
            <Input
              placeholder="Enter key"
              value={key}
              disabled={readOnly}
              onChange={(e) =>
                handleQueryParamChange(
                  keyName,
                  key,
                  "key",
                  e.target.value,
                  index
                )
              }
              style={{ width: 200 }}
            />
            {valueOptions.length > 0 ? (
              <Select
                placeholder="Select value"
                value={val}
                disabled={readOnly}
                style={{ width: 120 }}
                onChange={(v) =>
                  handleQueryParamChange(keyName, key, "value", v, index)
                }
              >
                {valueOptions.map(([optLabel, optVal]) => (
                  <Option key={optLabel} value={optVal}>
                    {optLabel}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder="Enter value"
                value={val}
                disabled={readOnly}
                style={{ width: 200 }}
                onChange={(e) =>
                  handleQueryParamChange(
                    keyName,
                    key,
                    "value",
                    e.target.value,
                    index
                  )
                }
              />
            )}
            {!readOnly && (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => deleteQueryParam(keyName, key, index)}
              />
            )}
          </Space>
        ))}

        {!readOnly && (
          <Button
            icon={<PlusOutlined />}
            onClick={() => addQueryParam(keyName, index)}
            size="small"
          >
            Add Param
          </Button>
        )}
      </div>
    );
  };

  const renderField = (field: FieldItem, index?: number, tabId?: string) => {
    if (field.interprete === "key_value_object") {
      const dbKey =
        tabId && isTokenTab(tabId)
          ? "token_endpoint_query_params"
          : field.db_key;
      return renderKeyValueObject(field, index, dbKey);
    }

    let value = "";
    if (typeof index === "number") {
      value = hrInfo["endpoints"]?.[index]?.[field.db_key] || "";
    } else if (tabId && isTokenTab(tabId)) {
      value = hrInfo.token_endpoint?.[field.db_key] || "";
    } else {
      value = hrInfo[field.db_key];
    }

    if (field.method === "dropdown" && field.values) {
      return (
        <Select
          value={value}
          placeholder={`Select ${field.name}`}
          disabled={readOnly}
          onChange={(val) => {
            if (readOnly) return;
            if (tabId && isTokenTab(tabId)) {
              handleTokenChange(field.db_key, val);
            } else if (typeof index === "number") {
              const endpoints = [...(hrInfo["endpoints"] || [])];
              endpoints[index][field.db_key] = val;
              setHrInfo({ ...hrInfo, endpoints });
            } else {
              handleChange(field.db_key, val);
            }
          }}
        >
          {Object.keys(field.values).map((opt) => (
            <Option key={opt} value={opt}>
              {opt}
            </Option>
          ))}
        </Select>
      );
    }

    return (
      <Input
        placeholder={`Enter ${field.name}`}
        value={value}
        disabled={readOnly || (mode === "edit" && field.db_key === "name")}
        onChange={(e) => {
          if (readOnly || (mode === "edit" && field.db_key === "name")) return;
          if (tabId && isTokenTab(tabId)) {
            handleTokenChange(field.db_key, e.target.value);
          } else if (typeof index === "number") {
            const endpoints = [...(hrInfo["endpoints"] || [])];
            endpoints[index][field.db_key] = e.target.value;
            setHrInfo({ ...hrInfo, endpoints });
          } else {
            handleChange(field.db_key, e.target.value);
          }
        }}
      />
    );
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    try {
      setLoading(true);
      const missingFields: string[] = [];

      ["name", "enable", "base_url"].forEach((key) => {
        if (!hrInfo[key] || hrInfo[key].toString().trim() === "") {
          missingFields.push(key);
        }
      });

      if (hrInfo.token_endpoint) {
        const { enable, uri, response_type } = hrInfo.token_endpoint;
        if (!enable) missingFields.push("token_endpoint.enable");
        if (!uri) missingFields.push("token_endpoint.uri");
        if (!response_type) missingFields.push("token_endpoint.response_type");
      }

      if (!Array.isArray(hrInfo.endpoints) || hrInfo.endpoints.length === 0) {
        missingFields.push("endpoints");
      }

      if (missingFields.length > 0) {
        openNotification({
          type: "warning",
          message: "Please fill all mandatory fields before saving.",
        });
        setLoading(false);
        return;
      }

      const { name, enable, base_url, endpoints, token_endpoint } = hrInfo;
      const payload = { name, enable, base_url, token_endpoint, endpoints };

      const method = mode === "edit" ? "put" : "post";
      const res = await axios({
        method,
        url: `${beAddress}/api/hr-info`,
        data: payload,
        headers: {
          "session-uid": sessionUid,
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
      });

      if (res.data?.status === "Success") {
        openNotification({
          type: "success",
          message:
            mode === "edit"
              ? "System configuration updated successfully"
              : "System configuration saved successfully",
        });
        navigate("/org-server-configuration", {
          state: { activeTab: "system" },
        });
      } else {
        openNotification({
          type: "error",
          message: "Failed to save system configuration",
        });
      }
    } catch (err: any) {
      console.error("Error saving HR info:", err);
      openNotification({
        type: "error",
        message: "Error while saving system configuration",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-system-config">
      <div className="header">
        <h3>
          {mode === "view"
            ? "View HR Config"
            : mode === "edit"
            ? "Edit HR Config"
            : "Add HR Config"}
        </h3>

        {readOnly && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setReadOnly(false);
              setMode("edit");
            }}
            className="hr-edit-btn"
          >
            Edit
          </Button>
        )}
      </div>

      {loading ? (
        <div className="loading">
          <Spin size="large" />
        </div>
      ) : (
        <div className="content">
          {tabs.map((tab) => (
            <div key={tab.tab_id} className="tab-section">
              <Divider>{tab.tab_name}</Divider>
              {tab.interprete === "group_of_objects" ? (
                <>
                  {(hrInfo["endpoints"] || []).map(
                    (endpoint: any, index: number) => (
                      <Card
                        key={index}
                        size="small"
                        className="endpoint-card"
                        title={`Endpoint ${index + 1}`}
                        extra={
                          !readOnly && (
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => deleteDataEndpoint(index)}
                            />
                          )
                        }
                      >
                        <div className="form-row">
                          {Object.entries(tab.filter).map(([key, field]) => (
                            <div className="form-field" key={key}>
                              <label className="form-label">
                                {field.name}
                                {field.mandatory && (
                                  <span className="mandatory">*</span>
                                )}
                              </label>
                              {renderField(field, index)}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )
                  )}
                  {!readOnly && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={addDataEndpoint}
                    >
                      Add Data Endpoint
                    </Button>
                  )}
                </>
              ) : (
                <div className="form-row">
                  {Object.entries(tab.filter).map(([key, field]) => (
                    <div className="form-field" key={key}>
                      <label className="form-label">
                        {field.name}
                        {field.mandatory && (
                          <span className="mandatory">*</span>
                        )}
                      </label>
                      {renderField(field, undefined, tab.tab_id)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!readOnly && (
            <div className="hr-submit-btn-container">
              <Button
                type="primary"
                onClick={handleSubmit}
                className="hr-submit-btn"
              >
                {mode === "edit" ? "Update HR Config" : "Save HR Config"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddSystemConfig;
